import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Alert,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Grid,
  Chip,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';

import '../../App.css';

const apiUrl = process.env.REACT_APP_API_URL;

const GROUPS = [
  {
    key: 'core',
    ar: 'الرئيسية والعملاء والخيل',
    en: 'Core, Owners & Horses',
    match: [
      'home',
      'horse',
      'horses',
      'owner',
      'client',
      'nfc',
      'pedigree',
      'statistics',
      'dashboard',
    ],
  },
  {
    key: 'operations',
    ar: 'السكن والتشغيل والنقل',
    en: 'Accommodation, Operations & Transport',
    match: [
      'stable',
      'room',
      'accommodation',
      'available',
      'transport',
      'horsetransport',
    ],
  },
  {
    key: 'clinic',
    ar: 'العيادة والفحوصات',
    en: 'Clinic & Checkups',
    match: [
      'clinic',
      'vet',
      'record',
      'checkup',
      'procedure',
      'medication',
      'approval',
    ],
  },
  {
    key: 'farrier',
    ar: 'البيطار',
    en: 'Farrier',
    match: ['farrier'],
  },
  {
    key: 'breeding',
    ar: 'الإنتاج والتلقيح',
    en: 'Breeding & Reproduction',
    match: ['reproduction', 'stallion', 'insemination', 'breeding'],
  },
  {
    key: 'finance',
    ar: 'المالية والمحاسبة',
    en: 'Finance & Accounting',
    match: [
      'account',
      'accountant',
      'finance',
      'ledger',
      'expense',
      'statement',
      'invoice',
    ],
  },
  {
    key: 'security',
    ar: 'المستخدمون والصلاحيات',
    en: 'Users & Permissions',
    match: ['user', 'role', 'permission', 'menu', 'profile', 'password'],
  },
  {
    key: 'settings',
    ar: 'الإعدادات والنظام',
    en: 'Settings & System',
    match: ['setting', 'system', 'alert', 'template', 'restore', 'activation'],
  },
  {
    key: 'other',
    ar: 'أخرى',
    en: 'Other',
    match: [],
  },
];

function normalizePageName(page) {
  return String(page || '').toLowerCase().replace(/[^a-z0-9\u0600-\u06ff]/g, '');
}

function getPermissionGroup(page) {
  const normalized = normalizePageName(page);
  return (
    GROUPS.find((group) =>
      group.key !== 'other' && group.match.some((token) => normalized.includes(normalizePageName(token)))
    ) || GROUPS[GROUPS.length - 1]
  );
}

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [pages, setPages] = useState([]);
  const [error, setError] = useState('');
  const [updatedPermissions, setUpdatedPermissions] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [newPageName, setNewPageName] = useState('');
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === 'ar';

  const groupedPages = useMemo(() => {
    const buckets = GROUPS.map((group) => ({ ...group, pages: [] }));
    const byKey = new Map(buckets.map((group) => [group.key, group]));

    pages.forEach((page) => {
      const group = getPermissionGroup(page);
      byKey.get(group.key)?.pages.push(page);
    });

    return buckets
      .map((group) => ({
        ...group,
        pages: group.pages.sort((a, b) => String(a).localeCompare(String(b), 'en', { numeric: true })),
      }))
      .filter((group) => group.pages.length > 0);
  }, [pages]);

  const enabledCount = useMemo(
    () => permissions.filter((permission) => Number(permission.can_access || 0) === 1).length,
    [permissions]
  );

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get(`${apiUrl}/roles`);
        setRoles(response.data);
      } catch (error) {
        console.error('Error fetching roles:', error);
        setError('Failed to fetch roles.');
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    if (selectedRole) {
      fetchPermissions(selectedRole);
    }
  }, [selectedRole]);

  const fetchPermissions = async (roleId) => {
    try {
      const response = await axios.get(
        `${apiUrl}/permissionsbyrols_id/${roleId}`
      );
      setPermissions(response.data);

      const uniquePages = [
        ...new Set(response.data.map((perm) => perm.page_name)),
      ].sort((a, b) => a.localeCompare(b));

      setPages(uniquePages);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to fetch permissions.');
    }
  };
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleCheckboxChange = (roleId, pageName) => {
    setPermissions((prevPermissions) => {
      return prevPermissions.map((perm) => {
        if (perm.role_id === roleId && perm.page_name === pageName) {
          return { ...perm, can_access: perm.can_access ? 0 : 1 };
        }
        return perm;
      });
    });

    setUpdatedPermissions((prev) => {
      const existing = prev.find(
        (p) => p.role_id === roleId && p.page_name === pageName
      );
      if (existing) {
        return prev.map((p) =>
          p.role_id === roleId && p.page_name === pageName
            ? { ...p, can_access: !p.can_access }
            : p
        );
      }

      // استخراج `permission_id` إن كان موجودًا في البيانات
      const existingPermission = permissions.find(
        (p) => p.role_id === roleId && p.page_name === pageName
      );
      return [
        ...prev,
        {
          permission_id: existingPermission
            ? existingPermission.permission_id
            : null,
          role_id: roleId,
          page_name: pageName,
          can_access: 1,
        },
      ];
    });
  };

  const handleSaveChanges = async () => {
    try {
      console.log(
        '🔹 Sending permissions for update:',
        JSON.stringify(updatedPermissions, null, 2)
      );

      if (!updatedPermissions || updatedPermissions.length === 0) {
        alert('❌ No changes detected.');
        return;
      }

      // التأكد من أن كل عنصر يحتوي على role_id و page_name قبل إرساله وتحويل can_access إلى 0 أو 1
      const updatePromises = updatedPermissions.map((perm) => {
        if (!perm.role_id || !perm.page_name) {
          console.error('❌ Missing role_id or page_name for:', perm);
          return Promise.reject('Missing role_id or page_name');
        }

        return axios.put(`${apiUrl}/permissions/${perm.permission_id}`, {
          role_id: perm.role_id,
          page_name: perm.page_name,
          can_access: perm.can_access ? 1 : 0, // تحويل true/false إلى 0/1
        });
      });

      await Promise.all(updatePromises);

      console.log('✅ All permissions updated successfully!');
      setError('');
      setSuccessMessage('✅ Permissions updated successfully!');
    } catch (error) {
      console.error(
        '❌ Error updating permissions:',
        error.response?.data || error.message
      );
      alert('❌ Error saving changes.');
    }
  };
  const handleAddPage = async () => {
    if (!newPageName.trim()) return alert('Page name is required.');

    try {
      await axios.post(`${apiUrl}/permissions/add-page`, {
        page_name: newPageName.trim(),
      });

      setSuccessMessage(`✅ Page '${newPageName}' added to all roles.`);
      setNewPageName('');

      if (selectedRole) {
        fetchPermissions(selectedRole);
      }
    } catch (error) {
      console.error('❌ Error adding new page:', error);
      setError('❌ Failed to add new page.');
    }
  };

  return (
    <Container component="main" maxWidth="lg">
      <Box sx={{ minHeight: '100vh', bgcolor: COLORS.page, py: 3 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {successMessage && <Alert severity="success">{successMessage}</Alert>}

        <Paper elevation={0} sx={{ ...cardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {t('role_permissions_title') || (isArabic ? 'صلاحيات الأدوار' : 'Role Permissions')}
              </Typography>
              <Typography sx={{ color: COLORS.muted, mt: 0.5 }}>
                {t('role_permissions_description') ||
                  (isArabic
                    ? 'إدارة صلاحيات النظام مرتبة حسب الاختصاص لتسهيل المراجعة.'
                    : 'Manage system access grouped by operational domain.')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip label={`${permissions.length} ${isArabic ? 'صلاحية' : 'permissions'}`} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }} />
              <Chip label={`${enabledCount} ${isArabic ? 'مفعلة' : 'enabled'}`} sx={{ bgcolor: COLORS.greenSoft, color: COLORS.green, fontWeight: 800 }} />
              <Chip label={`${groupedPages.length} ${isArabic ? 'اختصاصات' : 'groups'}`} sx={{ bgcolor: COLORS.blueSoft, color: COLORS.blue, fontWeight: 800 }} />
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          {/* اختيار الدور */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                🔽 {t('select_role')}
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.role_id} value={role.role_id}>
                      {role.role_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Paper>
          </Grid>

          {/* إضافة صفحة جديدة */}
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                ➕ {t('add_new_page')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label={t('page_name')}
                  value={newPageName}
                  onChange={(e) => setNewPageName(e.target.value)}
                  fullWidth
                />
                <Button variant="contained" onClick={handleAddPage}>
                  Add
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* جدول الصلاحيات */}
          <Grid item xs={12}>
            {selectedRole && (
              <Paper elevation={0} sx={{ ...cardSx, p: 2, overflow: 'hidden' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
                      {t('permissions_for_role')}
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.muted }}>
                      {isArabic ? 'تم ترتيب الصفحات حسب مجال العمل، مع الاحتفاظ بنفس مفاتيح الصلاحيات.' : 'Pages are grouped by work domain while keeping the same permission keys.'}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveChanges}
                    sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover } }}
                  >
                    {t('save_changes')}
                  </Button>
                </Stack>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow
                        sx={{
                          '& th': {
                            backgroundColor: COLORS.gold,
                            color: '#fff',
                            fontWeight: 700,
                            borderBottom: '2px solid #e6e0d4',
                            fontSize: '0.95rem',
                            letterSpacing: '.01rem',
                            textTransform: 'none',
                          },
                        }}
                      >
                        <TableCell>{t('page_name')}</TableCell>
                        <TableCell align="center">{t('access')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {groupedPages.map((group) => (
                        <React.Fragment key={group.key}>
                          <TableRow>
                            <TableCell colSpan={2} sx={{ bgcolor: COLORS.cardSoft, color: COLORS.gold, fontWeight: 900 }}>
                              {isArabic ? group.ar : group.en}
                              <Chip
                                size="small"
                                label={group.pages.length}
                                sx={{ mx: 1, bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }}
                              />
                            </TableCell>
                          </TableRow>
                          {group.pages.map((page) => {
                            const permission = permissions.find((p) => p.page_name === page);
                            return (
                              <TableRow key={page} hover sx={{ '& .MuiTableCell-root': { borderBottom: `1px solid ${COLORS.line}` } }}>
                                <TableCell>
                                  <Typography sx={{ color: COLORS.text, fontWeight: 800 }}>{page}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Checkbox
                                    checked={permission?.can_access ? true : false}
                                    onChange={() => handleCheckboxChange(selectedRole, page)}
                                    sx={{ color: COLORS.gold, '&.Mui-checked': { color: COLORS.green } }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default RolesPermissions;
